import React, { useState, useEffect } from 'react';
import { Form, Row, Col } from 'antd';
import RadioGroup from 'components/Form/RadioGroup';
import SliderField from 'components/Form/SliderField';
import Select from 'components/Form/Select';
import FormLabelWithField from 'components/Form/FormLabelWithField';
import Button from 'components/Button';
import GatedSubscribeForm from 'components/Tools/GatedSubscribeForm';
import { HvacFormContainer } from './styles';
import { FormContainer } from '../../styles';
import EmbedForm from 'components/Tools/EmbeddableForm';
import EmbedLink from 'components/Tools/EmbedLink';

import {
  ClimateRegion,
  InsulationGrade,
  SunExposure,
  AmountOfWindows,
  AirTightness,
  Occupants,
  ExtraDeviceWattage,
} from '../utils/content';
import useHvacClimateMap from 'hooks/useHvacClimateMap';

const HvacForm = ({ width, height, gatedForm, ...otherProps }) => {
  const pageImages = useHvacClimateMap();

  const mapImage =
    pageImages
      .filter(imageItem => imageItem.node.internalName === 'HVAC Climate Map')
      .map(imageItem => imageItem.node.image?.file?.url)[0] || '';

  const nameType = {
    mod_zone: 'Climate Region',
    mod_insulation: 'Insulation Grade',
    mod_sunny: 'Sun Exposure',
    mod_windows: 'Amount Of Windows',
    mod_tightness: 'Windows & Doors Air Tightness',
    mod_occupants: 'Occupants',
    mod_devices_quantity: 'Extra Device Heat Wattage',
    mod_area: 'Space Area (SQFT)',
    mod_height_percent: 'Space Height (FT)',
    mod_glassroom: 'Is Glass SunRoom?',
    mod_kitchen: 'Is there a kitchen',
  };

  const [values, setValues] = useState({
    mod_zone: '',
    mod_insulation: '',
    mod_sunny: '',
    mod_windows: '',
    mod_tightness: '',
    mod_occupants: '',
    mod_devices_quantity: '',
    mod_area: 100,
    mod_height_percent: 8,
    mod_glassroom: '',
    mod_kitchen: '',
  });

  const [formValid, setFormValid] = useState({
    mod_zone: 0,
    mod_insulation: 0,
    mod_sunny: 0,
    mod_windows: 0,
    mod_tightness: 0,
    mod_occupants: 0,
    mod_devices_quantity: 0,
    mod_area: 0,
    mod_height_percent: 0,
    mod_glassroom: 0,
    mod_kitchen: 0,
  });

  const [totalBtu, setTotalBtu] = useState(0);
  const [snapBtu, setSnapBtu] = useState(0);
  const [gateFormCookieIncrease, setGateFormCookieIncrease] = useState(0);
  const [isRecalc, setRecalc] = useState(false);
  const [isUpdate, setUpdate] = useState(false);

  const checkFieldValidate = (id, value) => {
    const fieldValidation = formValid;
    fieldValidation[id] = value !== '' ? 1 : -1;

    setFormValid(fieldValidation);
    return fieldValidation[id];
  };

  const checkValidate = () => {
    const keys = Object.keys(values);
    const stepValid = keys.filter(element => checkFieldValidate(element, values[element]) === 1)
      .length;
    return stepValid === keys.length;
  };

  const onHandleChange = (id, newValue) => {
    const tmpValues = Object.assign({}, values);
    tmpValues[id] = newValue;
    if (id === 'mod_glassroom' || id === 'mod_kitchen') {
      tmpValues[id] = Number(newValue);
    }
    setValues(tmpValues);

    checkFieldValidate(id, newValue);
  };

  const snapTo = btu => {
    const counts = [9000, 12000, 18000, 24000, 30000, 36000, 48000],
      goal = btu;
    const closest = counts.reduce(function(prev, curr) {
      return Math.abs(curr - goal) < Math.abs(prev - goal) ? curr : prev;
    });

    let start, end;
    if (goal <= counts[0]) {
      end = counts[0];
      return `${end.toLocaleString()}`;
    }

    if (goal > counts[counts.length - 1]) {
      start = counts[counts.length - 1];
      return `${start.toLocaleString()}`;
    }

    for (var i in counts) {
      if (counts[i] >= goal) {
        start = counts[i - 1];
        end = counts[i];
        break;
      }
    }

    return `${start.toLocaleString()}-${end.toLocaleString()}`;
  };

  const calcAll = () => {
    if (!checkValidate() || !isRecalc) {
      setUpdate(!isUpdate);
      return;
    }

    const {
      mod_zone,
      mod_insulation,
      mod_sunny,
      mod_windows,
      mod_tightness,
      mod_occupants,
      mod_devices_quantity,
      mod_area,
      mod_height_percent,
      mod_glassroom,
      mod_kitchen,
    } = values;

    const coolingbtusqft = 26;
    let mod_height = 0;
    if (mod_height_percent > 8) {
      mod_height = (mod_height_percent - 8) * 0.06;
    }
    let mod_devices = 0;
    if (mod_devices_quantity > 0) {
      mod_devices = (mod_devices_quantity / 100) * 340;
    }

    const base_btu = mod_area * coolingbtusqft;
    const mod_btu =
      mod_zone +
      mod_height +
      mod_insulation +
      mod_sunny +
      mod_windows +
      mod_tightness +
      mod_glassroom;
    const total_btu = base_btu + base_btu * mod_btu + mod_occupants + mod_kitchen + mod_devices;
    const btu_100 = Math.ceil(total_btu / 100) * 100;
    const btu_snap = snapTo(btu_100);
    setTotalBtu(btu_100);
    setSnapBtu(btu_snap);
    dataLayer.push({
      event: 'calculator',
      calculator_type: 'HVAC Calculator',
      climate_region: mod_zone,
      insulation_grade: mod_insulation,
      sun_exposure: mod_sunny,
      amount_of_windows: mod_windows,
      air_tightness: mod_tightness,
      number_of_occupants: mod_occupants,
      device_heat_wattage: mod_devices_quantity,
      space_area: mod_area,
      space_height: mod_height_percent,
      glass_room: mod_glassroom,
      kitchen: mod_kitchen,
      btu: btu_snap,
      calculated_cooling_load: btu_100.toLocaleString(),
    });

    setGateFormCookieIncrease(gateFormCookieIncrease + 1);
    setRecalc(false);
  };

  useEffect(() => {
    setRecalc(true);
  }, [values]);

  return (
    <FormContainer isEmbed={otherProps.isEmbed} {...otherProps}>
      <div className="section-tool-page-form--wrapper">
        <div className="section-tool-page-form--container">
          <HvacFormContainer width={width} height={height} {...otherProps}>
            <Row>
              <Col lg={12} md={12} sm={24} xs={24}>
                <Form className="hvac-form--form" data-position="hvac form">
                  <Form.Item className="hvac-form--form-item">
                    <FormLabelWithField
                      label="Climate Region"
                      isRequired={true}
                      helperText="Please select climate region"
                    >
                      <Select
                        id="mod_zone"
                        nameType={nameType['mod_zone']}
                        formValid={formValid}
                        placeholder="Select climate region"
                        options={ClimateRegion}
                        handleChange={onHandleChange}
                      />
                    </FormLabelWithField>
                  </Form.Item>

                  <Form.Item>
                    <SliderField
                      id="mod_area"
                      nameType={nameType['mod_area']}
                      min={100}
                      max={2000}
                      values={values}
                      formValid={formValid}
                      handleChange={onHandleChange}
                    />
                  </Form.Item>

                  <Form.Item>
                    <SliderField
                      id="mod_height_percent"
                      nameType={nameType['mod_height_percent']}
                      min={8}
                      max={100}
                      values={values}
                      formValid={formValid}
                      handleChange={onHandleChange}
                    />
                  </Form.Item>

                  <Form.Item className="hvac-form--form-item">
                    <FormLabelWithField
                      label="Insulation Grade"
                      isRequired={true}
                      helperText="Please select insulation grade"
                    >
                      <Select
                        id="mod_insulation"
                        nameType={nameType['mod_insulation']}
                        formValid={formValid}
                        placeholder="Select insulation grade"
                        options={InsulationGrade}
                        handleChange={onHandleChange}
                      />
                    </FormLabelWithField>
                  </Form.Item>

                  <Form.Item className="hvac-form--form-item">
                    <FormLabelWithField
                      label="Sun Exposure"
                      isRequired={true}
                      helperText="Please select sun exposure"
                    >
                      <Select
                        id="mod_sunny"
                        nameType={nameType['mod_sunny']}
                        formValid={formValid}
                        placeholder="Select sun exposure"
                        options={SunExposure}
                        handleChange={onHandleChange}
                      />
                    </FormLabelWithField>
                  </Form.Item>

                  <Form.Item className="hvac-form--form-item">
                    <FormLabelWithField
                      label="Amount of Windows"
                      isRequired={true}
                      helperText="Please select amount of windows"
                    >
                      <Select
                        id="mod_windows"
                        nameType={nameType['mod_windows']}
                        formValid={formValid}
                        placeholder="Select amount of windows"
                        options={AmountOfWindows}
                        handleChange={onHandleChange}
                      />
                    </FormLabelWithField>
                  </Form.Item>

                  <Form.Item className="hvac-form--form-item">
                    <FormLabelWithField
                      label="Windows & Doors Air Tightness"
                      isRequired={true}
                      helperText="Please select windows & doors air tightness"
                    >
                      <Select
                        id="mod_tightness"
                        nameType={nameType['mod_tightness']}
                        formValid={formValid}
                        placeholder="Select windows & doors air tightness"
                        options={AirTightness}
                        handleChange={onHandleChange}
                      />
                    </FormLabelWithField>
                  </Form.Item>

                  <Form.Item>
                    <RadioGroup
                      id="mod_glassroom"
                      nameType={nameType['mod_glassroom']}
                      options={[
                        { value: 0.4, label: 'Yes' },
                        { value: 0.0, label: 'No' },
                      ]}
                      values={values}
                      formValid={formValid}
                      // formErrors={formErrors}
                      handleChange={onHandleChange}
                    />
                  </Form.Item>

                  <Form.Item className="hvac-form--form-item">
                    <FormLabelWithField
                      label="How many Occupants?"
                      isRequired={true}
                      helperText="Please select occupants"
                    >
                      <Select
                        id="mod_occupants"
                        nameType={nameType['mod_occupants']}
                        formValid={formValid}
                        placeholder="Select occupants"
                        options={Occupants}
                        handleChange={onHandleChange}
                      />
                    </FormLabelWithField>
                  </Form.Item>

                  <Form.Item>
                    <RadioGroup
                      id="mod_kitchen"
                      nameType={nameType['mod_kitchen']}
                      options={[
                        { value: 4000, label: 'Yes' },
                        { value: 0, label: 'No' },
                      ]}
                      values={values}
                      formValid={formValid}
                      // formErrors={formErrors}
                      handleChange={onHandleChange}
                    />
                  </Form.Item>

                  <Form.Item className="hvac-form--form-item">
                    <FormLabelWithField
                      label="Extra Device Heat Wattage (W)"
                      isRequired={true}
                      helperText="Please select extra device heat wattage (W)"
                    >
                      <Select
                        id="mod_devices_quantity"
                        nameType={nameType['mod_devices_quantity']}
                        formValid={formValid}
                        placeholder="Select extra device heat wattage (W)"
                        options={ExtraDeviceWattage}
                        handleChange={onHandleChange}
                      />
                    </FormLabelWithField>
                  </Form.Item>
                </Form>
              </Col>
              <Col lg={12} md={12} sm={24} xs={24} className="sticky-container">
                <div className="hvac-form-right-container">
                  <img src={mapImage} className="hvac-form-map-image" />
                  <p className="hvac-form-map-description">RECOMMENDED EQUIPMENT CAPACITY</p>
                  <GatedSubscribeForm gatedForm={gatedForm} increaseCookie={gateFormCookieIncrease}>
                    <div className="hvac-form-right-gate-container">
                      <div className="hvac-form-total-calculation">
                        <p className="hvac-form-total-btu-range">{snapBtu} BTU</p>
                        <p className="hvac-form-total-btu-total">
                          Calculated Cooling Load:{' '}
                          <font color="red">{totalBtu.toLocaleString()} BTU</font>
                        </p>
                      </div>

                      <Button
                        dataPosition="Form - Calculate CTA"
                        className="hvac-form-calc-button"
                        onClick={() => calcAll()}
                        disabled={!isRecalc}
                      >
                        Calculate
                      </Button>
                    </div>
                  </GatedSubscribeForm>
                  <EmbedLink
                    show={!otherProps.isEmbed}
                    openCopyEmbedLinkDialog={otherProps?.openCopyEmbedLinkDialog}
                  />
                </div>
              </Col>
            </Row>
          </HvacFormContainer>
        </div>
      </div>
    </FormContainer>
  );
};

export const HvacCalcForm = EmbedForm(HvacForm);
